export const CARDAPIO_WORKSPACES = [
  {
    id: "overview",
    label: "Visão geral",
    description: "Status e pendências",
    kicker: "Resumo",
    longDescription:
      "Acompanhe a publicação dos canais, as pendências reais e os atalhos da operação.",
    defaultPanel: "overview",
    tabs: [],
  },
  {
    id: "catalog",
    label: "Catálogo",
    description: "Produtos e montagem",
    kicker: "Catálogo compartilhado",
    longDescription:
      "Organize o que aparece no cardápio sem perder de vista o cadastro usado pelo salão e pelo garçom.",
    defaultPanel: "catalog",
    tabs: [
      { id: "catalog", label: "Produtos" },
      { id: "categories", label: "Categorias" },
      { id: "options", label: "Complementos" },
      { id: "pizzas", label: "Variantes" },
    ],
  },
  {
    id: "offers",
    label: "Ofertas",
    description: "Campanhas, cupons e combos",
    kicker: "Vitrine comercial",
    longDescription:
      "Crie cada tipo de oferta no lugar certo e acompanhe sua publicação.",
    defaultPanel: "promotions",
    tabs: [
      { id: "promotions", label: "Campanhas" },
      { id: "coupons", label: "Cupons" },
      { id: "combos", label: "Combos" },
    ],
  },
  {
    id: "service",
    label: "Atendimento",
    description: "Canais e checkout",
    kicker: "Atendimento",
    longDescription:
      "Configure os canais e as regras que realmente interferem na compra do cliente.",
    defaultPanel: "channels",
    tabs: [
      { id: "channels", label: "Canais" },
      { id: "publishing", label: "Publicação e horários" },
      { id: "delivery", label: "Entrega" },
      { id: "pickup", label: "Retirada" },
      { id: "checkout", label: "Pagamentos" },
    ],
  },
  {
    id: "appearance",
    label: "Aparência e mensagens",
    description: "Marca, tema e textos",
    kicker: "Experiência do cliente",
    longDescription:
      "Personalize a identidade e confira no cardápio real antes de salvar.",
    defaultPanel: "visual",
    tabs: [],
  },
];

export const CARDAPIO_THEME_PRESETS = [
  {
    id: "nuts",
    label: "Original",
    description: "Uma paleta acolhedora e equilibrada para o cardápio.",
    primary: "#EE7361",
    accent: "#FFD993",
    background: "#FFF8F6",
  },
  {
    id: "natural",
    label: "Natural",
    description: "Verdes sóbrios para uma apresentação leve.",
    primary: "#2F6B4F",
    accent: "#BFE3CC",
    background: "#F5FAF7",
  },
  {
    id: "ocean",
    label: "Oceano",
    description: "Azuis frescos com bom contraste.",
    primary: "#176B87",
    accent: "#B9E6F2",
    background: "#F4FAFC",
  },
];

export const CARDAPIO_PREVIEW_SCENARIOS = [
  { value: "home", label: "Início" },
  { value: "empty", label: "Cardápio vazio" },
  { value: "closed", label: "Loja fechada" },
  { value: "checkout", label: "Checkout" },
  { value: "tracking", label: "Acompanhamento" },
];

const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

/**
 * Normaliza flags vindos de APIs legadas. `Boolean("0")` e
 * `Boolean("false")` seriam verdadeiros no JavaScript e já fizeram o painel
 * exibir canais/produtos com o estado oposto ao persistido.
 */
export function normalizeBoolean(value, fallback = false) {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  const normalized = String(value).trim().toLocaleLowerCase("pt-BR");
  if (["1", "true", "sim", "yes", "on", "ativo"].includes(normalized)) {
    return true;
  }
  if (
    ["0", "false", "nao", "não", "no", "off", "inativo", "null"].includes(
      normalized
    )
  ) {
    return false;
  }

  return fallback;
}

export function sameIdentifier(left, right) {
  if (left === undefined || left === null || left === "") return false;
  if (right === undefined || right === null || right === "") return false;
  return String(left) === String(right);
}

/**
 * Une o campo legado `produtos` com as coleções aditivas. A publicação vence
 * o candidato do mesmo produto para que uma resposta de transição nunca faça
 * um item publicado reaparecer como rascunho.
 */
export function mergeProductSources(...sources) {
  const byProduct = new Map();
  let anonymousIndex = 0;

  sources.forEach((source) => {
    (Array.isArray(source) ? source : []).forEach((product) => {
      if (!product || typeof product !== "object") return;

      const publicationHint =
        (product.cardapio_produto_id !== undefined &&
          product.cardapio_produto_id !== null &&
          product.cardapio_produto_id !== "") ||
        normalizeBoolean(product.publicado);
      const sourceId =
        product.produto_id ??
        product.source_id ??
        (!publicationHint ? product.id : null);
      const publicationId =
        product.cardapio_produto_id ?? (publicationHint ? product.id : null);
      const key =
        sourceId !== null && sourceId !== ""
          ? `produto:${sourceId}`
          : publicationId !== null && publicationId !== ""
          ? `publicacao:${publicationId}`
          : `anonimo:${anonymousIndex++}`;
      const current = byProduct.get(key);
      const currentIsPublication = Boolean(
        current &&
          (current.cardapio_produto_id || normalizeBoolean(current.publicado))
      );
      const nextIsPublication = Boolean(
        publicationId || normalizeBoolean(product.publicado)
      );

      if (!current || nextIsPublication || !currentIsPublication) {
        byProduct.set(key, current ? { ...current, ...product } : product);
      }
    });
  });

  return Array.from(byProduct.values());
}

export function parseBRLMoneyToCents(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  }

  let normalized = String(value ?? "")
    .trim()
    .replace(/[^0-9,.-]/g, "");

  if (!normalized) return 0;

  const negative = normalized.startsWith("-");
  normalized = normalized.replace(/-/g, "");

  if (normalized.includes(",")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else {
    const dotParts = normalized.split(".");
    if (dotParts.length > 2) {
      const decimal = dotParts.pop();
      normalized = `${dotParts.join("")}.${decimal}`;
    } else if (dotParts.length === 2 && dotParts[1].length === 3) {
      normalized = dotParts.join("");
    }
  }

  const amount = Number(normalized);
  if (!Number.isFinite(amount)) return 0;
  return Math.round((negative ? -amount : amount) * 100);
}

export function normalizeReadiness(value) {
  const readiness = value || {};

  return {
    pronto: normalizeBoolean(readiness.pronto),
    bloqueios: Array.isArray(readiness.bloqueios)
      ? readiness.bloqueios.filter(Boolean)
      : [],
    avisos: Array.isArray(readiness.avisos)
      ? readiness.avisos.filter(Boolean)
      : [],
  };
}

export function defaultWeeklyHours() {
  return Array.from({ length: 7 }, (_, day) => ({
    dia_semana: day,
    ativo: false,
    intervalos: [{ inicio: "18:00", fim: "23:00" }],
  }));
}

export function normalizeWeeklyHours(value) {
  const source = Array.isArray(value)
    ? value
    : Object.keys(value || {}).map((day) => {
        const dayValue = value[day] || {};
        return {
          dia_semana: Number(day),
          ativo: normalizeBoolean(dayValue.ativo, true),
          intervalos: dayValue.intervalos || dayValue,
        };
      });

  const extractIntervals = (day) => {
    const raw = day?.intervalos ?? day;
    const values = Array.isArray(raw) ? raw : raw ? [raw] : [];

    return values
      .map((interval) => ({
        inicio: interval?.inicio || "",
        fim: interval?.fim || "",
      }))
      .filter((interval) => interval.inicio || interval.fim);
  };

  const byDay = source.reduce((carry, day) => {
    const dayNumber = Number(day.dia_semana);

    if (dayNumber >= 0 && dayNumber <= 6) {
      const current = carry[dayNumber];
      const intervals = extractIntervals(day);

      if (!current) {
        carry[dayNumber] = {
          dia_semana: dayNumber,
          ativo: normalizeBoolean(day.ativo, true),
          intervalos: intervals,
        };
      } else {
        current.ativo = current.ativo || normalizeBoolean(day.ativo, true);
        current.intervalos.push(...intervals);
      }
    }

    return carry;
  }, {});

  return defaultWeeklyHours().map((fallback) => {
    const day = byDay[fallback.dia_semana];

    if (!day) return fallback;

    const intervals = extractIntervals(day).filter(
      (interval, index, all) =>
        all.findIndex(
          (candidate) =>
            candidate.inicio === interval.inicio &&
            candidate.fim === interval.fim
        ) === index
    );

    return {
      dia_semana: fallback.dia_semana,
      ativo: normalizeBoolean(day.ativo, true) && intervals.length > 0,
      intervalos: intervals.length ? intervals : fallback.intervalos,
    };
  });
}

export function validateWeeklyHours(value) {
  const normalized = normalizeWeeklyHours(value);
  const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/;
  const intervals = [];

  for (const day of normalized) {
    if (!day.ativo) continue;
    if (!day.intervalos.length) {
      return {
        valid: false,
        day: day.dia_semana,
        message: "Adicione ao menos um período ao dia aberto.",
      };
    }

    for (const interval of day.intervalos) {
      if (
        !timePattern.test(interval.inicio || "") ||
        !timePattern.test(interval.fim || "")
      ) {
        return {
          valid: false,
          day: day.dia_semana,
          message: "Preencha abertura e fechamento no formato HH:mm.",
        };
      }

      const toMinutes = (time) => {
        const [hour, minute] = time.split(":").map(Number);
        return hour * 60 + minute;
      };
      const start = day.dia_semana * 1440 + toMinutes(interval.inicio);
      const endInDay = toMinutes(interval.fim);
      const end =
        day.dia_semana * 1440 +
        endInDay +
        (endInDay <= toMinutes(interval.inicio) ? 1440 : 0);

      intervals.push({
        start,
        end,
        day: day.dia_semana,
        key: `${day.dia_semana}:${intervals.length}`,
      });
    }
  }

  const weekMinutes = 7 * 1440;
  const comparable = intervals.reduce((result, interval) => {
    result.push(
      interval,
      {
        ...interval,
        start: interval.start - weekMinutes,
        end: interval.end - weekMinutes,
      },
      {
        ...interval,
        start: interval.start + weekMinutes,
        end: interval.end + weekMinutes,
      }
    );
    return result;
  }, []);

  for (const interval of intervals) {
    const overlaps = comparable.some(
      (other) =>
        other.key !== interval.key &&
        interval.start < other.end &&
        other.start < interval.end
    );
    if (overlaps) {
      return {
        valid: false,
        day: interval.day,
        message: "Há períodos sobrepostos. Ajuste os horários antes de salvar.",
      };
    }
  }

  return { valid: true, day: null, message: "" };
}

function intervalLabel(interval) {
  if (interval.inicio === interval.fim) {
    return "24 horas";
  }

  return `${interval.inicio}–${interval.fim}`;
}

export function summarizeWeeklyHours(value) {
  const names = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const rangesByDay = normalizeWeeklyHours(value).reduce((result, day) => {
    if (day.ativo && day.intervalos.length) {
      result[day.dia_semana] = day.intervalos.map(intervalLabel).join(", ");
    }

    return result;
  }, {});
  const activeDays = Object.keys(rangesByDay).map(Number);

  if (!activeDays.length) return "";

  const uniqueRanges = new Set(Object.values(rangesByDay));
  if (activeDays.length === 7 && uniqueRanges.size === 1) {
    return `Todos os dias · ${Object.values(rangesByDay)[0]}`;
  }

  const parts = [];
  const grouped = new Set();
  const weekdays = [1, 2, 3, 4, 5];
  const weekdayRanges = weekdays.map((day) => rangesByDay[day]);

  if (weekdayRanges.every(Boolean) && new Set(weekdayRanges).size === 1) {
    parts.push(`Seg a sex · ${weekdayRanges[0]}`);
    weekdays.forEach((day) => grouped.add(day));
  }

  if (rangesByDay[0] && rangesByDay[6] && rangesByDay[0] === rangesByDay[6]) {
    parts.push(`Sáb e dom · ${rangesByDay[6]}`);
    grouped.add(0);
    grouped.add(6);
  }

  [1, 2, 3, 4, 5, 6, 0].forEach((day) => {
    if (rangesByDay[day] && !grouped.has(day)) {
      parts.push(`${names[day]} ${rangesByDay[day]}`);
    }
  });

  return parts.join(" · ");
}

export function applyWeeklyHoursPreset(value, preset) {
  const normalized = normalizeWeeklyHours(value);
  const firstActive = normalized.find(
    (day) => day.ativo && day.intervalos.length
  );
  const reference = (
    firstActive?.intervalos || [{ inicio: "18:00", fim: "23:00" }]
  ).map((interval) => ({ ...interval }));

  return normalized.map((day) => {
    if (preset === "always") {
      return {
        ...day,
        ativo: true,
        intervalos: [{ inicio: "00:00", fim: "00:00" }],
      };
    }

    const active =
      preset === "all" ||
      (preset === "weekdays" && day.dia_semana >= 1 && day.dia_semana <= 5);

    return {
      ...day,
      ativo: active,
      intervalos: active
        ? reference.map((interval) => ({ ...interval }))
        : day.intervalos,
    };
  });
}

export function serializeVisualDraft(form) {
  const value = form || {};

  return {
    logoUrl: value.logoUrl || null,
    capaUrl: value.capaUrl || null,
    removeLogo: Boolean(value.removeLogo),
    removeCapa: Boolean(value.removeCapa),
    hasLogoFile: Boolean(value.logoFile),
    hasCapaFile: Boolean(value.capaFile),
    primaryColor: value.primaryColor || "",
    accentColor: value.accentColor || "",
    backgroundColor: value.backgroundColor || "",
    cardStyle: value.cardStyle || "soft",
    buttonStyle: value.buttonStyle || "rounded",
    emptyText: value.emptyText || "",
    errorText: value.errorText || "",
    trackingText: value.trackingText || "",
  };
}

export function isValidHexColor(value) {
  return HEX_COLOR_PATTERN.test(String(value || "").trim());
}

export function normalizeHexColor(value, fallback) {
  const color = String(value || "").trim();
  return HEX_COLOR_PATTERN.test(color) ? color : fallback;
}

function hexToRgb(value) {
  const color = normalizeHexColor(value, "");

  if (!color) return null;

  const hex = color.slice(1);
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

function relativeLuminance(value) {
  const rgb = hexToRgb(value);

  if (!rgb) return null;

  const channel = (number) => {
    const normalized = number / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };

  return (
    0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
  );
}

export function colorContrast(colorA, colorB) {
  const luminanceA = relativeLuminance(colorA);
  const luminanceB = relativeLuminance(colorB);

  if (luminanceA === null || luminanceB === null) return 0;

  const light = Math.max(luminanceA, luminanceB);
  const dark = Math.min(luminanceA, luminanceB);
  return (light + 0.05) / (dark + 0.05);
}
